import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { tokenManager, userManager, User, AuthTokens } from '../utils/auth';
import { refreshAuthTokens } from '../utils/tokenRefresh';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;       // ID token — use for API Gateway Authorization header
  accessToken: string | null; // Access token — use for direct Cognito SDK calls
  login: (tokens: AuthTokens, user: User) => void;
  logout: () => void;
  setUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUserState] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const logout = useCallback(() => {
    tokenManager.clearTokens();
    userManager.clearUser();
    setIsAuthenticated(false);
    setToken(null);
    setAccessToken(null);
    setUserState(null);
    window.location.href = '/login';
  }, []);

  const login = useCallback((tokens: AuthTokens, newUser: User) => {
    tokenManager.saveTokens(tokens);
    userManager.saveUser(newUser);
    setIsAuthenticated(true);
    setToken(tokens.idToken);
    setAccessToken(tokens.accessToken);
    setUserState(newUser);
  }, []);

  const setUser = useCallback((newUser: User) => {
    userManager.saveUser(newUser);
    setUserState(newUser);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (tokenManager.isAuthenticated()) {
        const currentTokens = tokenManager.getTokens();
        setIsAuthenticated(true);
        setToken(currentTokens?.idToken ?? null);
        setAccessToken(currentTokens?.accessToken ?? null);
        setUserState(userManager.getUser());
        setIsLoading(false);
        return;
      }

      // Access token expired — try to refresh before giving up
      const refreshToken = tokenManager.getRefreshToken();
      if (refreshToken) {
        const newTokens = await refreshAuthTokens();
        if (newTokens) {
          setIsAuthenticated(true);
          setToken(newTokens.idToken);
          setAccessToken(newTokens.accessToken);
          setUserState(userManager.getUser());
          setIsLoading(false);
          return;
        }
      }

      // No valid session — don't clear tokens here; refreshAuthTokens() handles
      // permanent failures, and we don't want a transient network error wiping the cookie
      setIsLoading(false);
    };

    initAuth();

    // Background refresh every 5 minutes
    const interval = setInterval(async () => {
      const refreshToken = tokenManager.getRefreshToken();
      if (!refreshToken) return;

      if (!tokenManager.isAuthenticated() || tokenManager.shouldRefresh()) {
        const newTokens = await refreshAuthTokens();
        if (newTokens) {
          setToken(newTokens.idToken);
          setAccessToken(newTokens.accessToken);
        } else {
          // Refresh failed — clear context state so ProtectedRoute redirects to /login,
          // which runs initAuth() fresh and retries the refresh.
          // On transient failure: cookie is preserved, initAuth() will refresh successfully → back to dashboard.
          // On permanent failure: refreshAuthTokens() already cleared the cookie → login form shown.
          setIsAuthenticated(false);
          setToken(null);
          setAccessToken(null);
        }
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [logout]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, token, accessToken, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
